import { useEffect, useRef, useState } from 'react';
import { Grid, Row, Col } from '../../../utils/grid/Grid';
import FilterItem from './FilterItem';
import CheckboxList from '../../../utils/CheckboxList';

import './search-filter.css';
import {
  SearchFilter as SearchFilterInterface,
  SearchFilterKey,
  SearchFilterItem,
  SearchFilterOption,
  SearchOptionValue,
} from '../types';

interface SearchFilterProps {
  searchFilters: SearchFilterInterface & { accommodation?: SearchFilterItem };
  filterSet: {
    bedrooms: number;
    bathrooms: number;
    amenities: string[];
    distance_to_beach: number;
    accommodation_type: string[];
    stars: number;
  };
  onChange: (value: { [key: string]: SearchOptionValue | SearchOptionValue[] }) => void;
}

const activeFilterKeys: Readonly<SearchFilterKey[]> = [
  'bedrooms',
  'bathrooms',
  'amenities',
  'distance_to_beach',
  'accommodation_type',
  'stars',
];

interface SearchFilterState {
  openedFilter: string;
}

export default function SearchFilter({
  searchFilters,
  filterSet,
  onChange,
}: SearchFilterProps): JSX.Element {
  const el = useRef<HTMLDivElement>(null);
  const [filterState, setFilterState] = useState<SearchFilterState>({ openedFilter: '' });

  function isElementOutside(elem: Element): boolean {
    return !!el?.current && !el.current.contains(elem);
  }

  function onOutsideClick(e: MouseEvent): void {
    if (!e.target || !(e.target instanceof HTMLElement)) {
      return;
    }
    if (isElementOutside(e.target)) {
      // Is outside click
      setFilterState({ openedFilter: '' });
    }
  }

  function getFilterElements(): HTMLElement[] {
    if (!el || !el.current) {
      return [];
    }
    const elements = el.current.getElementsByClassName('search-filter-item');
    const htmlElements: HTMLElement[] = [];
    for (let i = 0; i < elements.length; i += 1) {
      const element = elements[i];
      if (element instanceof HTMLElement) {
        htmlElements.push(element);
      }
    }
    return htmlElements;
  }

  function getFocusedFilter(): HTMLElement | null | undefined {
    const { activeElement } = document;
    if (activeElement && el?.current && el.current.contains(activeElement)) {
      return getFilterElements().find((filterEl) => filterEl.contains(activeElement));
    }
    return null;
  }

  function onFocusIn(): void {
    const focusedFilter = getFocusedFilter();
    if (focusedFilter && focusedFilter.dataset.filter) {
      setFilterState({ openedFilter: focusedFilter.dataset.filter });
    }
  }

  useEffect(() => {
    document.addEventListener('click', onOutsideClick);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('click', onOutsideClick);
      document.removeEventListener('focusin', onFocusIn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onToggle(filter: string): void {
    const isAlreadyOpened = filterState.openedFilter === filter;

    setFilterState({
      openedFilter: isAlreadyOpened ? '' : filter,
    });
  }

  function isFilterSingleSelect(filterName: SearchFilterKey): boolean {
    return /bedrooms|bathrooms|distance_to_beach|stars/.test(filterName);
  }

  function handleOnChange(filterName: SearchFilterKey, selectedIndex: number | number[]): void {
    const filterOptions = searchFilters[filterName].options;
    const singleSelect = isFilterSingleSelect(filterName);
    if (singleSelect && typeof selectedIndex === 'number') {
      const selectedValue = filterOptions[selectedIndex].value;
      onChange({ [filterName]: selectedValue });
      return;
    }
    if (Array.isArray(selectedIndex)) {
      const selectedValue = selectedIndex.map((index) => {
        try {
          return filterOptions[index].value;
        } catch (error) {
          console.error(
            'Error processing change handler',
            filterName,
            filterOptions,
            selectedIndex,
          );
          throw error;
        }
      });
      onChange({ [filterName]: selectedValue });
    }
  }

  function isOpened(filterName: string): boolean {
    return filterState.openedFilter === filterName;
  }

  function getSelectedIndex(filterName: SearchFilterKey): number | number[] {
    const selectedValue = filterSet[filterName];
    const filterOptions = searchFilters[filterName].options;
    if (isFilterSingleSelect(filterName)) {
      return filterOptions.findIndex((option) => option.value === selectedValue);
    }
    if (typeof selectedValue === 'number') {
      return [];
    }
    return selectedValue.map((value) =>
      filterOptions.findIndex((option) => option.value === value),
    );
  }

  interface SingleFilterProps {
    filter: SearchFilterKey;
    wrapperProps: {
      label: string;
      opened: boolean;
      filterName: SearchFilterKey;
    };
    checkboxListProps: {
      radio: boolean;
      name: SearchFilterKey;
      options: SearchFilterOption[];
      selectedIndex: number | number[];
    };
  }

  function getFilterProps(): SingleFilterProps[] {
    return activeFilterKeys.map((filterName) => ({
      filter: filterName,
      wrapperProps: {
        label: searchFilters[filterName].title,
        opened: isOpened(filterName),
        filterName,
      },
      checkboxListProps: {
        radio: isFilterSingleSelect(filterName),
        name: filterName,
        options: searchFilters[filterName].options,
        selectedIndex: getSelectedIndex(filterName),
      },
    }));
  }

  return (
    <div ref={el}>
      <Grid className="search-filter">
        <Row>
          {getFilterProps().map(({ filter, wrapperProps, checkboxListProps }) => (
            <Col xs={12} md={4} className="search-filter-item" key={filter} data-filter={filter}>
              <FilterItem
                {...wrapperProps}
                onToggle={(filterName: string): void => onToggle(filterName)}
              >
                <CheckboxList
                  {...checkboxListProps}
                  onChange={(
                    option: SearchFilterOption,
                    selectedIndex: number | number[],
                  ): void => {
                    handleOnChange(filter, selectedIndex);
                  }}
                />
              </FilterItem>
            </Col>
          ))}
        </Row>
      </Grid>
    </div>
  );
}
